import { AddIcon } from '@chakra-ui/icons';
import { HStack } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useGetServiceCategories from './queries/useGetServiceCategories';
import useDeleteServiceCategory from './queries/useDeleteServiceCategory';

import { Button, ConfirmationDialog, Text } from '@/components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';

import { getServiceCategoriesTableColumns } from './serviceCategoriesTableConfig';

export default function ServiceCategories() {
    const gridRef = useRef();
    const navigate = useNavigate();

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const [serviceCategoryToDelete, setServiceCategoryToDelete] =
        useState(null);

    const handleEditServiceCategory = useCallback(
        (serviceCategoryData) => {
            navigate(`/service-categories/edit/${serviceCategoryData.id}`);
        },
        [navigate],
    );

    const handleAddServiceCategory = () => {
        navigate('/service-categories/add');
    };

    const handleDeleteServiceCategory = useCallback((serviceCategoryData) => {
        setServiceCategoryToDelete(serviceCategoryData);
    }, []);

    const handleCloseDeleteDialog = () => {
        setServiceCategoryToDelete(null);
    };

    const handleDeleteSuccess = () => {
        toast.success('Service category deleted successfully!');
        setServiceCategoryToDelete(null);
    };

    const handleDeleteError = () => {
        toast.error('Failed to delete service category. Please try again.');
        setServiceCategoryToDelete(null);
    };

    const { mutate: deleteServiceCategory, isPending: isDeleting } =
        useDeleteServiceCategory(handleDeleteSuccess, handleDeleteError);

    const handleConfirmDelete = () => {
        if (serviceCategoryToDelete) {
            deleteServiceCategory(serviceCategoryToDelete.id);
        }
    };

    const baseColumns = useMemo(
        () =>
            getServiceCategoriesTableColumns({
                onEditServiceCategory: handleEditServiceCategory,
                onDeleteServiceCategory: handleDeleteServiceCategory,
            }),
        [handleEditServiceCategory, handleDeleteServiceCategory],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching } = useGetServiceCategories(
        setTableData,
        gridRef,
        baseColumns,
    );

    const isFetchingData = isRefetching || isFetching;

    const titleName = 'Service Categories';

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    Service Categories
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                titleName={titleName}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            leftIcon={<AddIcon />}
                            onClick={handleAddServiceCategory}
                            size="sm"
                            padding="6px 12px">
                            Add Service Category
                        </Button>
                    </HStack>
                }
            />

            <ConfirmationDialog
                isOpen={!!serviceCategoryToDelete}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDelete}
                title="Delete Service Category"
                message={`Before deleting a service category, please ensure it is not in use by any service profiles or vendor defined services. Are you sure you want to delete "${serviceCategoryToDelete?.name || 'this service category'}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmColorScheme="red"
                isLoading={isDeleting}
            />
        </>
    );
}
